import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Upload } from 'lucide-react';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AddVendorDialog from '@/components/AddVendorDialog';
import ImportDialog from './vendors/ImportDialog';
import UncategorizedPanel from './vendors/UncategorizedPanel';

/** The rolodex (PRD §F1): a lookup table, not a sales CRM. */
export default function VendorsPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await client.api.categories.$get();
      if (!res.ok) throw new Error('Failed to load categories');
      return res.json();
    },
  });

  const { data: vendors, isLoading } = useQuery({
    queryKey: ['vendors', search, category],
    queryFn: async () => {
      const res = await client.api.vendors.$get({ query: { q: search, category } });
      if (!res.ok) throw new Error('Failed to load vendors');
      return res.json();
    },
  });

  // Only meaningful when no category filter is applied — a filter by name
  // already excludes every uncategorized vendor, so there is nothing here to
  // duplicate or contradict.
  const uncategorized = category === '' ? (vendors ?? []).filter((v) => !v.primaryCategory) : [];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="display-tight text-3xl font-semibold">Vendors</h1>
          <p className="text-sm text-muted-foreground">
            The people you trust, searchable, in one place.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="size-4" />
            Import vendors
          </Button>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="size-4" />
            Add vendor
          </Button>
        </div>
      </div>

      <UncategorizedPanel
        vendors={uncategorized}
        categories={(categories ?? []).map((c) => c.name)}
      />

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search name, contact, email, or notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={category || 'all'} onValueChange={(v) => setCategory(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-52">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(categories ?? []).map((c) => (
              <SelectItem key={c.id} value={c.name}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : vendors && vendors.length > 0 ? (
        <div className="grid gap-2">
          {vendors.map((v) => (
            <Link key={v.id} to={`/vendors/${v.id}`}>
              <Card className="rounded-2xl bg-card px-5 py-4 shadow-ambient transition-shadow hover:shadow-lg">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium">{v.businessName}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {v.contactName && `${v.contactName} · `}
                      {v.emails.length > 0
                        ? (v.emails.find((e) => e.isPrimary) ?? v.emails[0])!.email
                        : 'No email on file'}
                      {v.emails.length > 1 && ` +${v.emails.length - 1}`}
                    </p>
                  </div>
                  {v.primaryCategory && (
                    <span className="label-caps rounded-full bg-[var(--color-surface-container-high)] px-2.5 py-0.5 text-xs">
                      {v.primaryCategory}
                    </span>
                  )}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card className="rounded-3xl bg-card p-10 text-center shadow-ambient">
          <p className="display-tight text-lg font-semibold">
            {search || category ? 'Nothing matches' : 'No vendors yet'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {search || category
              ? 'Try a different search or category.'
              : 'Add one by hand, or import the spreadsheet you already have.'}
          </p>
        </Card>
      )}

      <AddVendorDialog open={addOpen} onOpenChange={setAddOpen} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
}
